import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGlobalTheme } from '../../hooks/useGlobalTheme';
import { useLayout } from '../../../core/contexts/LayoutContext';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import Header from './Header';
import LicenseModulesChangeNotifier from '../LicenseModulesChangeNotifier';
import { isCloudDemoRuntime } from '../../../shared/utils/publicDemoRuntime';
import { FACTURATION_SECTION_ARCHIVE_ROUTES } from '../../../pages/facturation/config/facturationSectionArchivesConfig';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import SaudiLayout from '../../../products/saudi/layout/SaudiLayout';

/** Cloud demo: parent shell is 100dvh; avoid min-h-screen + h-screen stacking a second page scrollbar. */
const IS_CLOUD_DEMO = isCloudDemoRuntime();
const demoShellOuter = IS_CLOUD_DEMO ? 'flex flex-col flex-1 min-h-0 h-full' : 'min-h-screen';
const demoShellInner = IS_CLOUD_DEMO ? 'h-full min-h-0' : 'h-screen';

const Layout = ({ children }) => {
  if (isSaudiBrand()) {
    return (
      <>
        <LicenseModulesChangeNotifier />
        <SaudiLayout>{children}</SaudiLayout>
      </>
    );
  }

  return <DefaultLayoutShell>{children}</DefaultLayoutShell>;
};

function DefaultLayoutShell({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true); // Default to open on desktop
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // New state for collapsed mode
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false); // Separate state for mobile
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load
  const { i18n } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDashboardLightMode } = useGlobalTheme();
  const { isSidebarMode, isTopBarMode } = useLayout();
  const isAgentDocumentEmbed = searchParams.get('embed') === 'agent';
  
  // Mark that initial load is done
  React.useEffect(() => {
    const timer = setTimeout(() => setIsInitialLoad(false), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Consider RTL if the language starts with "ar" OR the document direction is explicitly set to RTL
  const isRTL = (i18n.language && i18n.language.startsWith('ar')) || document.documentElement.dir === 'rtl';
  
  // Check if we're on pages that support light theme
  const isDashboardPage = location.pathname === '/' || location.pathname === '/dashboard';
  const isOverviewPage = location.pathname === '/overview';
  const isDocumentsPage =
    location.pathname === '/archives' ||
    FACTURATION_SECTION_ARCHIVE_ROUTES.includes(location.pathname);
  const isPaymentsPage = location.pathname === '/payments';
  const isReportsPage = location.pathname.startsWith('/reports');
  const isStockPage = location.pathname === '/stock';
  const isReturnsPage = location.pathname === '/returns';
  const isFinancesPage = location.pathname === '/finances';
  const isClientsPage = location.pathname === '/clients';
  const isEmployeesPage = location.pathname === '/employees';
  const isDepartmentsPage = location.pathname === '/departments';
  // const isDeliveryTransportPage = location.pathname === '/delivery-transport'; // DELETED
  const isProductsPage = location.pathname === '/products';
  /** Catalogue services (Facturation) — même shell clair que Produits ; sinon logo « dark » sur fond clair en local */
  const isServicesPage =
    location.pathname === '/services' || location.pathname.startsWith('/services/');
  const isCategoriesPage = location.pathname === '/categories';
  const isAnalysisPage = location.pathname.startsWith('/analysis');
  const isSettingsPage = location.pathname === '/settings';
  const isDocumentCreationPage =
    location.pathname === '/document-creation' ||
    location.pathname.startsWith('/document-creation/') ||
    location.pathname === '/inventaire/document-creation' ||
    location.pathname === '/creer-un-document';

  /** Sous-modules Facturation (Analyses & Rapports, etc.) — même shell clair que le dashboard */
  const isFacturationModulePage = location.pathname.startsWith('/facturation');
  /**
   * Démo / prod Cloudflare : routes « pretty » sous `/f/...` (ex. `/f/dashboard`) — même shell clair
   * que `/facturation/...` ; sinon Layout garde le shell sombre et le sidebar charge le logo « dark ».
   */
  const isFacturationFShell = location.pathname.startsWith('/f/');
  
  // Pages that can use the dashboard “light” shell (fond clair + padding).
  // Nouveau Document : même cohérence que le reste de l’app en mode clair (header / shell).
  const isThemePage = isDashboardPage || isOverviewPage || isDocumentsPage || isPaymentsPage || isReportsPage || 
                     isStockPage || isReturnsPage || isFinancesPage || isClientsPage || 
                     isEmployeesPage || isDepartmentsPage || // isDeliveryTransportPage (DELETED) ||
                     isProductsPage || isServicesPage || isCategoriesPage || isAnalysisPage || 
                     isSettingsPage;
  const shouldApplyLightTheme =
    (isThemePage || isDocumentCreationPage || isFacturationModulePage || isFacturationFShell) && isDashboardLightMode;
  const isDocumentCreationFullBleed = isDocumentCreationPage;

  // Function to toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Function to toggle sidebar collapsed state
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Function to toggle mobile sidebar
  const toggleMobileSidebar = () => {
    setSidebarMobileOpen(!sidebarMobileOpen);
  };

  if (isAgentDocumentEmbed) {
    return (
      <div
        className={`erp-app-root h-full min-h-0 overflow-hidden transition-colors duration-300 ease-in-out ${
          shouldApplyLightTheme
            ? 'bg-gradient-to-br from-gray-50 to-white'
            : 'bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900'
        } ${isRTL ? 'rtl' : 'ltr'}`}
      >
        <main className="h-full min-h-0 w-full overflow-y-auto overflow-x-hidden p-0">{children}</main>
      </div>
    );
  }

  return (
    <div className={`erp-app-root ${demoShellOuter} transition-colors duration-300 ease-in-out ${shouldApplyLightTheme
        ? 'bg-gradient-to-br from-gray-50 to-white' 
        : 'bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900'
      } ${isRTL ? 'rtl' : 'ltr'}`}>
      
      {/* TOP BAR MODE */}
      {isTopBarMode && (
          <div className={`flex flex-col ${demoShellInner}`}>
          <TopBar isLightTheme={shouldApplyLightTheme} />
          <main
            className={
              isDocumentCreationFullBleed
                ? 'flex-1 overflow-y-auto overflow-x-hidden min-w-0 pt-6 px-6 pb-8'
                : 'flex-1 overflow-y-auto overflow-x-hidden min-w-0 pt-8 px-8 pb-12'
            }
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={isInitialLoad ? { duration: 0 } : { duration: 0.3 }}
              className="w-full min-w-0 max-w-full"
            >
              {children}
            </motion.div>
          </main>
        </div>
      )}

      {/* SIDEBAR MODE */}
      {isSidebarMode && (
        <div className={`flex ${demoShellInner} overflow-hidden min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Desktop Sidebar */}
          <AnimatePresence mode="wait">
            <motion.div
              key="sidebar"
              initial={false}
              animate={{ width: sidebarOpen ? (sidebarCollapsed ? 96 : 256) : 0 }}
              exit={{ width: 0 }}
              transition={isInitialLoad ? { duration: 0 } : { duration: 0.2, ease: 'easeInOut' }}
              className={`${isRTL ? 'order-2' : 'order-1'} relative z-30 hidden lg:block`}
            >
              <Sidebar 
                sidebarOpen={sidebarOpen} 
                sidebarCollapsed={sidebarCollapsed}
                toggleSidebar={toggleSidebar} 
                toggleSidebarCollapsed={toggleSidebarCollapsed}
                isLightTheme={shouldApplyLightTheme} 
              />
            </motion.div>
          </AnimatePresence>

          {/* Main Content */}
          <div className={`flex-1 flex flex-col overflow-hidden min-w-0 ${isRTL ? 'order-1' : 'order-2'}`}>
            {/* Header */}
            <Header 
              sidebarOpen={sidebarOpen} 
              toggleSidebar={toggleSidebar}
              sidebarMobileOpen={sidebarMobileOpen}
              toggleMobileSidebar={toggleMobileSidebar}
              isLightTheme={shouldApplyLightTheme}
            />

            {/* Main Content Area */}
            <main 
              className={
                isDocumentCreationFullBleed
                  ? `flex-1 overflow-y-auto overflow-x-hidden min-w-0 pt-6 px-6 pb-8 main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`
                  : `flex-1 overflow-y-auto overflow-x-hidden min-w-0 pt-8 px-8 pb-12 main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`
              }
              style={{ 
                minHeight: 0,
                background: isDocumentCreationFullBleed ? 'transparent' : undefined
              }}
            >
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={isInitialLoad ? { duration: 0 } : { duration: 0.3 }}
                className="w-full min-w-0 max-w-full"
              >
                {children}
              </motion.div>
            </main>
          </div>

          {/* Mobile Sidebar */}
          <AnimatePresence>
            {sidebarMobileOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={toggleMobileSidebar}
                  className="fixed inset-0 bg-black/50 z-20 lg:hidden"
                />
                <motion.div
                  initial={{ x: isRTL ? '100%' : '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: isRTL ? '100%' : '-100%' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-30 w-64 lg:hidden`}
                >
                  <Sidebar sidebarOpen={true} toggleSidebar={toggleMobileSidebar} isMobile={true} isLightTheme={shouldApplyLightTheme} />
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )}
      <LicenseModulesChangeNotifier />
    </div>
  );
};

export default Layout;

